import BaseService from '../../base/base_service';
import { HISTORY_TYPE } from './history_const';
import _ from '../../utils/underscore';
import IHistoryService, { IDailyDealCampaign } from './history_service_interface';

export interface IHistoryServiceParams {
    uri: string;
    gameSessionUri: string;
    recentlyPlayedUri: string;
    aiRecommendedGameListUri: string;
    popupCampaignsUrl: string;
}

export interface IHistoryPageInfo {
    pageNumber: number;
    pageSize?: number;
    fromLastDays: number;
    requestKey?: string;
    minDate?: string;
    maxDate?: string;
}

export interface IGameHistoryRoundData {
    GameId: string;
    GameTypeId: string;
    ProductPackageID: number;
    SubBrandId?: number;
    VendorName: string;
    CompanyName: string;
    GameGroupId: number;
    Date: string;
    TimeSincePlay: string;

    BetAmount: number;
    AmountCurrencyISOCode: string;
    AvailableMoneyPerOffering: number;
    AllowedPlatformType: string;

    DateInTimeZone?: string;
    FreePlayCurrentAmount?: number;
    FreePlayWinningAmount?: number;
    FreePlayReachedMaxWinCap?: boolean;
    OriginalWinningsAmount?: number;

    ProfitAmount: number;
    VatBetFee: number;
    IsBigWin: boolean;

    IsSupportDetailedHistory: boolean;
    AdditionalData: any;
}

export interface IHistoryServerData {
    GameHistoryData: IGameHistoryRoundData[];

    PagingData?: Array<{
        PageNumber: number
    }>;

    isLastPage?: boolean;
}

export default class HistoryService extends BaseService implements IHistoryService {
    static CLASS = 'HistoryService';

    private _params: IHistoryServiceParams;


    init(params: IHistoryServiceParams) {
        super.init(params);
        this._params = params;
    }


    getNextPage(historyType: number, pageInfo: IHistoryPageInfo): Promise<IHistoryServerData> {
        return historyType === HISTORY_TYPE.Regular
            ? this._callHistoryApi(pageInfo, this._params.uri)
            : this._callHistoryApi(pageInfo, this._params.gameSessionUri);
    }


    getRecentlyPlayed(): Promise<{gameTypes: number[]}> {
        return this.serviceFactory.http2Service().post(this._params.recentlyPlayedUri);
    }

    /**
     * Gets the AI Recommended Game List from the server / platform
     * @returns {Promise|Object{RecommendedGameList : Object}
     */
    getAIRecommendedGameList(): Promise<{[gameType: number]: number}> {
        return new Promise((resolve) => {
            this.serviceFactory.http2Service().post(this._params.aiRecommendedGameListUri).then((data: any) => {
                try {
                    if (typeof data !== 'object')
                        resolve({});

                    resolve(data.RecommendedGameList);
                } catch (e) {
                    this.logger.error('Failed to parse get AI recommended list response, error:' + e);
                    resolve({});
                }
            });
        });
    }


    private _callHistoryApi(pageInfo: IHistoryPageInfo, uri: string): Promise<IHistoryServerData> {
        return this.serviceFactory.oAuth2Service().waitForAutologin().then(() => {
            if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
                throw new Error('Cannot retrieve History - Users not authenticated!');
            }
            return this.serviceFactory.http2Service().post<IHistoryServerData>(uri, pageInfo as any);
        });
    }


    public getDailyDealCampaigns(): Promise<IDailyDealCampaign[]> {
        if (_.isEmpty(this._params.popupCampaignsUrl)) {
            return Promise.resolve([]);
        }
        return this.serviceFactory.http2Service().post(this._params.popupCampaignsUrl)
            .then((data: any) => (data && data.MarketingCampaigns) || []);
    }
}
